/**
 * EbookUploader Component
 * ========================
 * Production-ready drag & drop upload for PDF, EPUB, MOBI files.
 * Features: progress bar, validation, checksum display, preservation status.
 */

import { useState, useRef, useCallback } from 'react';
import { Upload, X, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import ebookService from '@/services/ebookService';

const ALLOWED_EXTENSIONS = ['.pdf', '.epub', '.mobi'];
const MAX_SIZE_MB = 200;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

export default function EbookUploader({ submissionId, onUploadComplete, disabled = false }) {
  const [isDragging, setIsDragging] = useState(false);
  const [uploadState, setUploadState] = useState('idle'); // idle, validating, uploading, processing, done, error
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const validateFile = (file) => {
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      return `Invalid file type. Allowed: ${ALLOWED_EXTENSIONS.join(', ')}`;
    }
    
    if (file.size > MAX_SIZE_BYTES) {
      return `File too large. Maximum: ${MAX_SIZE_MB}MB`;
    }
    
    return null;
  };

  const handleUpload = useCallback(async (file) => {
    if (!submissionId) {
      toast.error('Please select a submission first');
      return;
    }

    setError(null);
    setUploadState('validating');
    
    // Validate
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      setUploadState('error');
      toast.error(validationError);
      return;
    }

    setUploadState('uploading');
    setUploadProgress(0);

    try {
      const result = await ebookService.uploadEbook(
        submissionId,
        file,
        (progress) => {
          setUploadProgress(progress);
          if (progress >= 100) {
            setUploadState('processing');
          }
        }
      );

      setUploadState('done');
      setUploadProgress(100);
      toast.success('eBook uploaded successfully!');
      
      if (onUploadComplete) {
        onUploadComplete(result);
      }

      // Reset after delay
      setTimeout(() => {
        setUploadState('idle');
        setUploadProgress(0);
      }, 2000);

    } catch (err) {
      setError(err.message);
      setUploadState('error');
      toast.error(err.message || 'Upload failed');
    }
  }, [submissionId, onUploadComplete]);

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    if (disabled) return;
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleUpload(files[0]);
    }
  };

  const handleFileSelect = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleUpload(files[0]);
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleBrowseClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const renderUploadState = () => {
    switch (uploadState) {
      case 'validating':
        return (
          <div className="flex items-center gap-2 text-[#b77900]">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Validating file...</span>
          </div>
        );
      
      case 'uploading':
        return (
          <div className="w-full space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-[#b77900] font-medium">Uploading...</span>
              <span className="text-slate-600">{uploadProgress}%</span>
            </div>
            <Progress value={uploadProgress} className="h-2" />
          </div>
        );
      
      case 'processing':
        return (
          <div className="flex items-center gap-2 text-[#b77900]">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Processing & computing checksum...</span>
          </div>
        );
      
      case 'done':
        return (
          <div className="flex items-center gap-2 text-green-600">
            <CheckCircle2 className="w-5 h-5" />
            <span>Upload complete!</span>
          </div>
        );
      
      case 'error':
        return (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-red-600">
              <AlertCircle className="w-5 h-5" />
              <span>{error}</span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setUploadState('idle');
                setError(null);
              }}
            >
              Try Again
            </Button>
          </div>
        );
      
      default:
        return (
          <>
            <Upload className={`w-12 h-12 mx-auto mb-4 ${isDragging ? 'text-[#c98905]' : 'text-[#c98905]'}`} />
            <p className="text-sm font-medium text-slate-700 mb-1">
              {isDragging ? 'Drop file here' : 'Drag & drop your eBook'}
            </p>
            <p className="text-xs text-slate-500 mb-4">
              or click to browse
            </p>
            <Button
              onClick={handleBrowseClick}
              disabled={disabled}
              variant="outline"
              className="border-[#d8bd7a] bg-white text-[#9a6500] hover:bg-[#fff8eb]"
            >
              Browse Files
            </Button>
            <p className="text-xs text-slate-400 mt-4">
              Supported: PDF, EPUB, MOBI • Max: {MAX_SIZE_MB}MB
            </p>
          </>
        );
    }
  };

  return (
    <div
      data-testid="ebook-uploader"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`
        relative p-8 border-2 border-dashed rounded-xl text-center transition-all duration-200
        ${isDragging 
          ? 'border-[#c98905] bg-[#fff8eb]'
          : uploadState === 'error'
            ? 'border-red-300 bg-red-50'
            : uploadState === 'done'
              ? 'border-green-300 bg-green-50'
              : 'border-[#d8bd7a] bg-[#fffaf2] hover:border-[#c98905] hover:bg-[#fff8eb]'
        }
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
      `}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.epub,.mobi"
        onChange={handleFileSelect}
        className="hidden"
        disabled={disabled}
      />
      
      {renderUploadState()}
    </div>
  );
}
